// ©2024 Austin App House. All rights reserved.
/* eslint-disable import/no-cycle */
import React, { useEffect, useRef, useState } from 'react';
import { Switch, Redirect, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import socketIOClient from 'socket.io-client';
import { IntercomProvider, useIntercom } from 'react-use-intercom';
import { init, setCurrentUser } from '../storage/actions/authActions';
import AuthRoute from '../components/layout/CustomRouts/AuthRoute';
import ControlPanel from '../components/layout/ControlPanel';
import CertificatesContractors from './admin/CertificatesContractors';
import ClientsManageFunds from './admin/ClientsManageFunds';
import ManageCompanies from './admin/ManageCompanies';
import FundingSources from './admin/FundingSources';
import CreateAndEditWorkOrderTemplates from './clients/CreateAndEditWorkOrderTemplates';
import CreateAndEditWorkOrder from './clients/CreateAndEditWorkOrder';
import ManageCustomFields from './clients/ManageCustomFields';
import PMTools from './clients/PMTools';
import ClientPaymentMethods from './clients/PaymentMethods';
import FindContractor from './clients/FindContractor';
import ContractorGroups from './clients/ContractorGroups';
import UserManagement from './clients/UserManagement';
import ManageTeams from './clients/MangeTeams';
import Templates from './clients/Templates';
import Recruits from './admin/Recruits';
import Contractors from './admin/Contractors';
import CertificationTestVideo from './contractors/certification/CertificationTestVideo';
import CertificationTests from './contractors/certification/CertificationTests';
import Certifications from './contractors/certifications/Certifications';
// import JobsDashboard from './JobsDashboard';
import ManageFunds from './ManageFunds';
import Dashboard from './Dashboard';
import { getProfile } from '../storage/actions/profile';
import VerificationComplete from './VerificationComplete';
import RemoteMount from '../microfrontends/RemoteMount';
import Loader from '../components/dashboard/common/Loader';

// Публичные страницы Next (remote на :3010). Dashboard и app — только в CRA ниже.
const NEXT_PUBLIC_PATHS = [
  '/home',
  '/about',
  '/contact',
  '/signin',
  '/signup',
  '/privacy',
  '/terms',
  '/support',
  '/coverage',
];

const LEGACY_PATH_REDIRECTS = {
  '/': '/home',
  '/about_us': '/about',
  '/locate_techs': '/coverage',
  '/find_work': '/signup',
  '/solution': { pathname: '/home', hash: '#solutions' },
  '/faq': '/support',
  '/sign_in': '/signin',
  '/register': '/signup',
  '/mf-demo': '/home',
};

/** Next remote рендерит главную как `/`, в адресной строке host — `/home`. */
const nextRemotePathname = (pathname) => (pathname === '/home' ? '/' : pathname);

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;
const PORT = process.env.REACT_APP_MODE
  ? process.env.REACT_APP_ENDPOINT_PORT_BUILD
  : process.env.REACT_APP_ENDPOINT_PORT_DEBUG;
const PORT2 = process.env.REACT_APP_MODE
  ? process.env.REACT_APP_ENDPOINT2_PORT_BUILD
  : process.env.REACT_APP_ENDPOINT2_PORT_DEBUG;



const ENDPOINT = `${window.location.protocol}//${window.location.hostname}:${PORT}`;
const ENDPOINT2 = `${window.location.protocol}//${window.location.hostname}:${PORT2}`;

export const SocketContext = React.createContext({
  chatSocket: null,
  notificationSocket: null,
});

const Pages = ({ auth, getProfile }) => {
  const { pathname, search } = useLocation();
  const isNextPublic = NEXT_PUBLIC_PATHS.includes(pathname);
  const isLegacyRedirect = Object.prototype.hasOwnProperty.call(LEGACY_PATH_REDIRECTS, pathname);
  const isPublicGuestPage = isNextPublic
    || isLegacyRedirect
    || pathname === '/verification-complete';
  const [chatSocket, setSocketForChat] = useState();
  const [notificationSocket, setSocketForNotification] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const landingRef = useRef([]);
  const otherRef = useRef([]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      const token = window.localStorage.getItem('jwtToken');
      const chatConnection = token
        ? socketIOClient(ENDPOINT, {
          query: {
            token,
          },
          withCredentials: true,
          transports: ['websocket', 'polling']
        })
        : null;
      if (chatConnection) {
        chatConnection.on('connect', () => {
          console.log('messaging connected');
        });
      }
      const notificationConnection = token
        ? socketIOClient(ENDPOINT2, {
          query: {
            token,
          },
          withCredentials: true,
          transports: ['websocket', 'polling']
        })
        : null;

      if (notificationConnection) {
        notificationConnection.on('connect', () => {
          console.log('notifications connected');
        });
      }
      setSocketForNotification(notificationConnection);
      // console.log('chatConnetion XXXXXXXXXXXXX------->', chatConnection);
      setSocketForChat(chatConnection);
    }
  }, [auth]);


  useEffect(() => {
    if (!auth.isAuthenticated && !isPublicGuestPage) {
      window.location.href = '/home';
    }
  }, [auth.isAuthenticated, isPublicGuestPage]);

  useEffect(() => {
    if (auth.user?.id) {
      getProfile(auth.user?.id);
    }

  }, [auth.user?.id]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      document.body.classList.remove('dashboard-body');
      return undefined;
    }
    document.body.classList.add('dashboard-body');
    return () => document.body.classList.remove('dashboard-body');
  }, [auth.isAuthenticated]);

  // App styles for authenticated CRA pages (marketing styles come from the Next remote).

  useEffect(() => {
    if (!auth.isAuthenticated && (isNextPublic || isLegacyRedirect)) {
      setIsLoading(false);
      return undefined;
    }

    window.scrollTo(0, 0);

    if (landingRef.current.length) {
      landingRef.current.forEach((node) => node.parentNode && node.parentNode.removeChild(node));
      landingRef.current = [];
    }

    if (!otherRef.current.length) {
        setIsLoading(true);
        const beforeCount = document.querySelectorAll('style, link[rel="stylesheet"]').length;
        Promise.all([
          import('../sass/home/index.scss'),
          import('../sass/home/index-l.scss'),
          import('../sass/home/index-m.scss'),
          import('../sass/order/order.scss'),
          import('../sass/order/order-l.scss'),
          import('../sass/order/order-m.scss'),
          import('../sass/find_contractor/find_contractor.scss'),
          import('../sass/find_contractor/find_contractor-l.scss'),
          import('../sass/find_contractor/find_contractor-m.scss'),
          import('../sass/smallpages/smallpages.scss'),
          import('../sass/smallpages/smallpages-l.scss'),
          import('../sass/smallpages/smallpages-m.scss'),
        ])
          .then(() => import('../sass/custom_styles/wo_details.css'))
          .then(() => import('../sass/custom_styles/select.scss'))
          .then(() => import('../sass/custom_styles/custom_styles.scss'))
          .then(() => import('../sass/custom_styles/dashboard-modals.css'))
          .then(() => import('../sass/custom_styles/dashboard-responsive.css'))
          .then(() => {
          setIsLoading(false);
          const all = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'));
          const added = all.slice(beforeCount);
          added.forEach((n) => n.setAttribute('data-style', 'other'));
          otherRef.current = added;
        }).catch((err) => {
          console.error(err);
          setIsLoading(false);
        });
    }

    return undefined;
  }, [pathname, auth.isAuthenticated]);
  const mainContainer = useRef();

  return (
      <div ref={mainContainer} id="qweewq" className={auth.isAuthenticated ? 'dashboard-layout' : undefined}>
        {!auth.isAuthenticated && isLegacyRedirect ? (
          <Redirect to={LEGACY_PATH_REDIRECTS[pathname]} />
        ) : !auth.isAuthenticated && isNextPublic ? (
          <RemoteMount
            module="nextjsRemote/mount"
            remoteProps={{ pathname: nextRemotePathname(pathname), search }}
          />
        ) : pathname === '/verification-complete' ? (
          <VerificationComplete />
        ) : isLoading ? (
          <div className="dashboard-page-loader">
            <Loader size="lg" className="wurqly-spiral-loader-wrap--page" />
          </div>
        ) : (
          <>
            {auth.isAuthenticated && (
              <IntercomProvider appId={INTERCOM_APP_ID} autoBoot>
              <SocketContext.Provider value={{ chatSocket, notificationSocket }}>
              <ControlPanel>
              <Switch>
                <>
                    <AuthRoute
                      userType="Redirect"
                      auth={auth}
                      exact
                      path="/"
                      render={() => <Redirect to="/dashboard" />}
                    />
                    <AuthRoute
                      key="Dashboard"
                      auth={auth}
                      exact
                      path={['/dashboard', '/dashboard/:tab']}
                      component={() => (
                        <Dashboard
                          mainContainer={mainContainer}
                          useIntercom={useIntercom}
                        />
                      )}
                    />
                    <AuthRoute
                      key="ManageFunds"
                      auth={auth}
                      path="/manage-funds"
                      hasNoPermissionRoles={['Manager', 'Dispatcher']}
                      component={() => <ManageFunds mainContainer={mainContainer} />}
                    />
                    {/* Client routes */}
                    <AuthRoute
                      key="ManageCustomFields"
                      auth={auth}
                      userType="client"
                      exact
                      path="/manage-custom-fields"
                      component={() => (
                        <ManageCustomFields mainContainer={mainContainer} />
                      )}
                    />
                    {/*<AuthRoute*/}
                    {/*  key="PMTools"*/}
                    {/*  auth={auth}*/}
                    {/*  userType="client"*/}
                    {/*  exact*/}
                    {/*  path="/PMTools"*/}
                    {/*  component={() => (*/}
                    {/*    <PMTools mainContainer={mainContainer} />*/}
                    {/*  )}*/}
                    {/*/>*/}
                    <AuthRoute
                      key="ManageTeams"
                      auth={auth}
                      userType="client"
                      exact
                      path="/manage-teams"
                      component={() => <ManageTeams mainContainer={mainContainer} />}
                    />
                    <AuthRoute
                      key="FindContractor"
                      auth={auth}
                      userType="client"
                      exact
                      path="/find-contractor"
                      component={() => (
                        <FindContractor mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="ContractorGroups"
                      auth={auth}
                      userType="client"
                      exact
                      path="/contractor-groups"
                      component={() => (
                        <ContractorGroups mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="UserMang"
                      auth={auth}
                      userType="client"
                      exact
                      path="/user-management"
                      hasNoPermissionRoles={['Manager', 'Dispatcher']}
                      component={() => (
                        <UserManagement mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="CreateAndEditWorkOrder"
                      auth={auth}
                      userType="client"
                      exact
                      path={['/create-work-order', '/create-work-order/:id']}
                      component={() => (
                        <CreateAndEditWorkOrder mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="Template"
                      auth={auth}
                      userType="client"
                      exact
                      path="/templates"
                      component={() => <Templates mainContainer={mainContainer} />}
                    />
                    <AuthRoute
                      key="CreateAndEditWorkOrderTemplate"
                      auth={auth}
                      userType="client"
                      exact
                      path={[
                        '/create-work-order-template',
                        '/create-work-order-template/:id',
                      ]}
                      component={() => (
                        <CreateAndEditWorkOrderTemplates
                          mainContainer={mainContainer}
                        />
                      )}
                    />
                    <AuthRoute
                      key="PaymentMethods"
                      auth={auth}
                      userType="client"
                      path="/payment-methods"
                      hasNoPermissionRoles={['Manager', 'Dispatcher']}
                      component={() => (
                        <ClientPaymentMethods mainContainer={mainContainer} />
                      )}
                    />
                    {/* Client routs end */}
                    {/* Contractor routs */}
                    <AuthRoute
                      key="Certifications"
                      auth={auth}
                      exact
                      path="/certifications"
                      userType="contractor"
                      component={() => (
                        <Certifications mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="WatchVideo"
                      auth={auth}
                      userType="contractor"
                      path="/certifications/:id/video"
                      component={() => (
                        <CertificationTestVideo mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="CertificationTests"
                      auth={auth}
                      userType="contractor"
                      path="/certifications/:id/tests"
                      component={() => (
                        <CertificationTests mainContainer={mainContainer} />
                      )}
                    />
                    {/* Contractor routs end */}
                    {/* Admin routs */}
                    <AuthRoute
                      key="CertificatesContractors"
                      auth={auth}
                      userType="superAdmin"
                      exact
                      path="/certificates-contractors"
                      component={() => (
                        <CertificatesContractors mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="Contractors"
                      auth={auth}
                      userType="superAdmin"
                      exact
                      path="/contractors"
                      component={() => (
                        <Contractors mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="Recruits"
                      auth={auth}
                      userType="superAdmin"
                      exact
                      path="/recruits"
                      component={() => <Recruits mainContainer={mainContainer} />}
                    />
                    <AuthRoute
                      key="ManageCompanies"
                      auth={auth}
                      userType="superAdmin"
                      exact
                      path="/manage-companies"
                      component={() => (
                        <ManageCompanies mainContainer={mainContainer} />
                      )}
                    />
                    <AuthRoute
                      key="ClientsManageFunds"
                      auth={auth}
                      userType="superAdmin"
                      exact
                      path="/platform-fee-transactions"
                      component={() => (
                        <ClientsManageFunds mainContainer={mainContainer} />
                      )}
                    />
                    {/* Admin routs end */}
                    <AuthRoute
                      key="FundingSources"
                      auth={auth}
                      userTypes={['superAdmin']}
                      exact
                      path="/funding-sources"
                      hasNoPermissionRoles={['Manager', 'Dispatcher']}
                      component={() => (
                        <FundingSources mainContainer={mainContainer} />
                      )}
                    />
                  <AuthRoute
                    key="default"
                    auth={auth}
                    exact
                    path="*"
                    render={() => <Redirect to="/dashboard" />}
                  />
                </>
              </Switch>
              </ControlPanel>
              </SocketContext.Provider>
              </IntercomProvider>
            )}
          </>

        )}

      </div>
  );
};

const mapDispatchToProps = {
  setCurrentUser,
  init,
  getProfile,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, mapDispatchToProps)(Pages);
